/* eslint-disable */
// @ts-nocheck
import env from "./env";
import { jwtDecode } from "jwt-decode";

export const initializeAnalytics = (): void => {
  if (!env.SEGMENT_KEY) {
    console.warn("SEGMENT_KEY not found, analytics will not be initialized");
    return;
  }

  if (!env.IS_STAGING && !env.IS_PROD) {
    console.warn(
      "Analytics will not be initialized because it is available only in staging and production"
    );
    return;
  }

  const i = "analytics";
  const analytics = (window[i] = window[i] || []);

  if (!analytics.initialize) {
    if (analytics.invoked) {
      window.console &&
        console.error &&
        console.error("Segment snippet included twice.");
    } else {
      analytics.invoked = true;
      analytics.methods = [
        "trackSubmit",
        "trackClick",
        "trackLink",
        "trackForm",
        "pageview",
        "identify",
        "reset",
        "group",
        "track",
        "ready",
        "alias",
        "debug",
        "page",
        "screen",
        "once",
        "off",
        "on",
        "addSourceMiddleware",
        "addIntegrationMiddleware",
        "setAnonymousId",
        "addDestinationMiddleware",
        "register",
      ];

      analytics.factory = function (e: string) {
        return function () {
          if (window[i].initialized)
            return window[i][e].apply(window[i], arguments);
          const n = Array.prototype.slice.call(arguments);
          if (
            ["track", "screen", "alias", "group", "page", "identify"].indexOf(
              e
            ) > -1
          ) {
            const c = document.querySelector("link[rel='canonical']");
            n.push({
              __t: "bpc",
              c: (c && c.getAttribute("href")) || void 0,
              p: location.pathname,
              u: location.href,
              s: location.search,
              t: document.title,
              r: document.referrer,
            });
          }
          n.unshift(e);
          analytics.push(n);
          return analytics;
        };
      };

      for (let n = 0; n < analytics.methods.length; n++) {
        const key = analytics.methods[n];
        analytics[key] = analytics.factory(key);
      }

      // @ts-expect-error - Any type needed for Segment load options
      analytics.load = function (key: string, n?: any) {
        const t = document.createElement("script");
        t.type = "text/javascript";
        t.async = true;
        t.setAttribute("data-global-segment-analytics-key", i);
        t.src =
          "https://cdn.segment.com/analytics.js/v1/" +
          key +
          "/analytics.min.js";
        const r = document.getElementsByTagName("script")[0];
        r.parentNode?.insertBefore(t, r);
        analytics._loadOptions = n;
      };

      analytics._writeKey = env.SEGMENT_KEY;
      analytics.SNIPPET_VERSION = "5.2.0";
      analytics.load(env.SEGMENT_KEY);

      identifyCurrentUser();
    }
  }
};

interface UserProperties {
  /**
   * MadKudu user ObjectId from `mkClaims.userId`. This — never the email — is
   * the Segment userId: `sub` on the JWT is the email, so identifying by it
   * leaves Amplitude without a stable user id (CPF-1199).
   */
  userId: string | undefined;
  email: string | undefined;
  domain: string | undefined;
  tenant: number | undefined;
  role: string | undefined;
}

const EMPTY_USER_PROPERTIES: UserProperties = {
  userId: undefined,
  email: undefined,
  domain: undefined,
  tenant: undefined,
  role: undefined,
};

export const extractUserProperties = (): UserProperties => {
  try {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      return EMPTY_USER_PROPERTIES;
    }

    const decoded = jwtDecode(accessToken);
    const email = decoded.email || decoded.sub;
    const domain = email ? email.split("@")[1] : undefined;
    const tenant = decoded.mkClaims?.tenantId;
    // v2 RBAC tenants have an empty `permissions[]`, so the role lives on
    // `rbac`. Falling back keeps v1 tenants working.
    const role =
      decoded.mkClaims?.rbac?.role ?? decoded.mkClaims?.permissions?.[0]?.role;

    return {
      userId: decoded.mkClaims?.userId,
      email,
      domain,
      tenant,
      role,
    };
  } catch (error) {
    console.error("Error extracting user properties from JWT", error);
    return EMPTY_USER_PROPERTIES;
  }
};

/**
 * Identify the current user to Segment from the stored access token. Safe to
 * call repeatedly — call it again whenever the token changes (impersonation
 * start/end, refresh) so the traits track the active identity.
 */
export const identifyCurrentUser = (): void => {
  if (!window.analytics) {
    return;
  }

  try {
    const { userId, ...traits } = extractUserProperties();

    if (!userId) {
      return;
    }

    window.analytics.identify(userId, traits);
  } catch (error) {
    console.error("Error identifying user to Segment", error);
  }
};

/**
 * Clears the identified user from Segment. Call on logout so a subsequent
 * session on the same browser isn't attributed to the previous user.
 */
export const resetAnalytics = (): void => {
  if (!window.analytics) {
    return;
  }

  try {
    window.analytics.reset();
  } catch (error) {
    console.error("Error resetting analytics", error);
  }
};

/**
 * Track a custom Segment event. Event-scoped properties only — email/domain/
 * role are user traits sent via identify (CPF-1199). `tenant` is duplicated on
 * the event, like on page views, because events are filtered by it.
 */
export const trackEvent = (
  event: string,
  properties: Record<string, unknown> = {}
): void => {
  if (!window.analytics) {
    console.warn("Analytics not initialized");
    return;
  }

  try {
    window.analytics.track(event, {
      tenant: extractUserProperties().tenant,
      app: "market-analyzer",
      path: window.location.pathname,
      url: window.location.href,
      ...properties,
    });
  } catch (error) {
    console.error(`Error tracking event ${event}`, error);
  }
};

export const trackPage = (pathname?: string): void => {
  if (!window.analytics) {
    console.warn("Analytics not initialized");
    return;
  }

  try {
    // Event-scoped properties only. email/domain/role are user traits and are
    // sent via identify (CPF-1199); `tenant` is kept because page views are
    // filtered by it.
    const properties = {
      tenant: extractUserProperties().tenant,
      app: "market-analyzer",
      path: pathname || window.location.pathname,
    };

    // Send page view with "Market Analyzer" as the page name (first argument)
    window.analytics.page("Market Analyzer", properties);
  } catch (error) {
    console.error("Error tracking page view", error);
  }
};
